import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  useClearSafety,
  useConfirmLoad,
  useConfirmUnload,
  useForgetModuleGrips,
  useInitializeModule,
  useLoads,
  useMe,
  useModules,
  usePreflightBatch,
  useRecordWasteReplacement,
  useUnloads,
  useWasteContainers,
  type ApiError,
} from '@ctcells/api'
import type {
  ModuleSnapshot,
  PreflightBlocker,
  PreflightResult,
  PreflightWarning,
  TrayLoad,
} from '@ctcells/contract'
import { errorMessage, t } from '@ctcells/i18n'

import { Button } from '../atoms/Button.tsx'
import { gate, type ActionGate, type ScreenAccess } from '../screens/access.ts'
import { LoadConfirmWithDefault } from '../screens/batches/LoadConfirmModal.tsx'
import type { LoadConfirmReport } from '../screens/batches/loadConfirmReport.ts'
import {
  PreflightReport,
  type PreflightRowExtra,
  type PreflightReportProps,
} from '../screens/batches/PreflightReport.tsx'
import { ReasonActionButton } from '../screens/modules/ModuleControlRows.tsx'
import {
  FORGET_GRIPS_OPERATIONS,
  initializeGate,
  moduleWriteGate,
} from '../screens/modules/moduleInitializeGate.ts'
import {
  preflightFixesFor,
  supplyKindOf,
  preflightRowKey,
  type PreflightFixKind,
} from './preflightFixes.ts'
import { QUICK_LOAD_WAIT_MS } from '../screens/batches/devQuickBatch.ts'
import { InstallLabwareFix } from './InstalledLabwareFix.tsx'
import { HandoffReleaseFix } from './HandoffReleaseFix.tsx'
import { HeldTrayFix } from './HeldTrayFix.tsx'
import { holdsTrayAtHandoff, type HeldTrayResult } from './heldTrayRecreate.ts'
import { StopsReleaseFix } from './StopsReleaseFix.tsx'
import { resolveHandoffPoint } from './handoffPoint.ts'
import { SupplyFix } from './SupplyFix.tsx'
import { lbw09Target } from './installedLabwareModel.ts'
import { useConfirmSource } from './surfaceContext.ts'

import './startPreflightActions.css'

/**
 * 작업 줄 시작 창의 사전 점검 — 줄마다 **그 자리에서 누를 조치**(eric 2026-10-23 「작업 시작 버튼을 누르면.. 지금 사전 점검에서
 * 체크해야 하는 것들을 버튼 형태로 만들어줄래?」 · 「버튼을 만들어서.. 초기화하도록 하는 거 팝업창에서 정리해줘」).
 *
 * ```
 * ┌ 시작 차단 ───────────────────────────────────────────┐
 * │ ▲ 트레이 자리 점유 · 염색기 슬라이드   [수거 확인] [자리 정리]│
 * │   └ 수거 확인 — 받았습니다 · 다시 점검합니다               │
 * │ ▲ 트레이 미적재 · 염색기               [적재 완료]           │
 * │ ─ 경고 ─  △ 시약 여유 0 µL             [소모품 판으로 →]      │
 * │ [사전 점검 다시]                                     [닫기] │
 * └──────────────────────────────────────────────────────┘
 * ```
 *
 * - 「초기화」 계열은 모듈 서랍 「제어」 카드와 **같은 훅 · 같은 게이트**다(`initializeGate` · `moduleWriteGate` · `ReasonActionButton`)
 * - 🔴 조치가 끝나면 **점검만** 다시 돌린다(`usePreflightBatch`). 작업 줄의 시작 흐름(`useStartGate.begin`)은 통과 + 경고 0건이면
 *   곧바로 시작을 보내므로 여기서 쓰지 않는다 — 다시 점검했는데 작업이 시작되면 안 된다
 * - 권한 · 제어락 · 끊김이면 숨기지 않고 비활성 + 사유다. «전부 정리»는 없다 — 사람이 항목별로 누른다
 * - 🔴 **작업을 끊는 조치는 하나뿐이다** — 6축이 쥔 채 멈춘 줄의 [작업 중단 후 내려놓기](`HeldTrayFix.tsx`). 그 단추는 이름 · 확인 창이
 *   «이 작업을 중단하고 같은 내용으로 다시 만든다»를 말하고, 끝나면 창이 **새 작업**으로 점검을 잇는다(`batchId` 가 바뀐다)
 * - 🔵 조치 뒤 다시 돈 점검에 «적재 확약 대기»(`PREFLIGHT_TRAY_NOT_LOADED`)가 남으면 **한 번만** `QUICK_LOAD_WAIT_MS` 뒤 다시 점검한다 —
 *   수거 확인 · 정리 직후엔 서버가 적재 건을 아직 안 열었다(히나 실측 2026-10-23 · 재생성 직후 한 번 더 떴다). 주기 폴링이 아니다
 */
interface RowState {
  state: 'running' | 'done' | 'failed'
  text: string
}

type Row = PreflightBlocker | PreflightWarning

export function FixablePreflightReport({
  initial,
  batchId: openedBatchId,
  access,
  onPassed,
  presentation = 'list',
  hideTitle = false,
}: {
  presentation?: PreflightReportProps['presentation']
  hideTitle?: boolean | undefined
  initial: PreflightResult
  batchId: string
  access: ScreenAccess
  /**
   * 다시 돈 점검이 통과했다 — 시작 창이 같은 창의 다음 얼굴(경고 확약 → [시작])로 넘어간다(eric 2026-10-23 「팝업창에서.. 다 해결」).
   * `batchId` 는 그 결과를 낸 작업이다
   */
  onPassed?: ((result: PreflightResult, batchId: string) => void) | undefined
}) {
  /* 🔴 창이 작업을 다시 만들면(작업 중단 후 내려놓기) 그 뒤 조치 · 점검은 새 작업으로 간다 */
  const [batchId, setBatchId] = useState(openedBatchId)
  const [result, setResult] = useState<PreflightResult>(initial)
  /** 줄이 사라져도 남는 결과 한 줄 — 작업을 다시 만들면 옛 줄이 목록에서 빠진다 */
  const [flowNotice, setFlowNotice] = useState<RowState | null>(null)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => {
    const timer = retryTimer
    return () => clearTimeout(timer.current)
  }, [])
  const [rows, setRows] = useState<Readonly<Record<string, RowState>>>({})
  const [loadTarget, setLoadTarget] = useState<{ key: string; load: TrayLoad } | null>(null)
  const recheckMutation = usePreflightBatch()
  const loads = useLoads({ batch_id: batchId })
  const unloads = useUnloads({ pending: true })
  const modules = useModules({})
  const waste = useWasteContainers({})
  const { operations } = useMe()
  const confirmSource = useConfirmSource()
  const confirmLoad = useConfirmLoad()
  const confirmUnload = useConfirmUnload()
  const initialize = useInitializeModule()
  const clearSafety = useClearSafety()
  const forget = useForgetModuleGrips()
  const recordWaste = useRecordWasteReplacement()
  const setRow = (key: string, next: RowState) => setRows((all) => ({ ...all, [key]: next }))

  /** 점검 결과를 받는다 — 통과면 시작 창이 다음 얼굴로 넘어간다(시작은 사람이 [시작]을 눌러야 나간다) */
  const adopt = (next: PreflightResult, of: string) => {
    setBatchId(of)
    setResult(next)
    if (next.passed) onPassed?.(next, of)
  }

  /**
   * 🔴 점검만 — 결과를 이 창 안에 다시 그린다. 시작은 보내지 않는다.
   * `retryTrayLoad` — 조치 뒤 점검에 적재 확약 대기가 남으면 한 번만 잠시 뒤 다시 본다(머리말)
   */
  const recheck = (refreshTrays = false, retryTrayLoad = false, of: string = batchId) => {
    clearTimeout(retryTimer.current)
    recheckMutation.mutate(
      { batchId: of },
      {
        onSuccess: (next) => {
          if (refreshTrays) {
            void loads.refetch()
            void unloads.refetch()
          }
          adopt(next, of)
          if (retryTrayLoad && trayLoadPending(next))
            retryTimer.current = setTimeout(() => {
              void loads.refetch()
              recheck(true, false, of)
            }, QUICK_LOAD_WAIT_MS)
        },
      },
    )
  }

  /** 조치 한 건 — 진행 · 결과를 그 줄에 적고, 받았으면 점검을 다시 돌린다 */
  const run = (key: string, action: string, send: () => Promise<unknown>, refreshTrays = false) => {
    setRow(key, { state: 'running', text: t('pfFix.running', { action }) })
    void send().then(
      () => {
        setRow(key, { state: 'done', text: t('pfFix.done', { action }) })
        recheck(refreshTrays, true)
      },
      (error: unknown) => {
        setRow(key, {
          state: 'failed',
          text: t('pfFix.failed', { action, reason: errorMessage(error as ApiError) }),
        })
      },
    )
  }

  /** [작업 중단 후 내려놓기] — 끊기 · 내려놓기 · 다시 만들기 · 새 작업 점검을 그 줄에서 돌리고, 결과는 창 위 한 줄에도 남긴다 */
  const runHeld = (
    key: string,
    flow: (onStatus: (text: string) => void) => Promise<HeldTrayResult>,
  ) => {
    const action = t('pfHeld.action')
    const status = (text: string) => setRow(key, { state: 'running', text })
    status(t('pfFix.running', { action }))
    setFlowNotice(null)
    const finish = (next: RowState) => {
      setRow(key, next)
      setFlowNotice(next)
    }
    void flow(status).then(
      (out) => {
        if (out.ok) {
          const batch = out.batchId.slice(0, 8)
          finish(
            out.placeFailed === undefined
              ? { state: 'done', text: t('pfHeld.done', { batch }) }
              : {
                  state: 'failed',
                  text: t('pfHeld.donePlaceFailed', { batch, reason: out.placeFailed }),
                },
          )
          void unloads.refetch()
          adopt(out.result, out.batchId)
          return
        }
        if (out.batchId !== undefined) setBatchId(out.batchId)
        finish({
          state: 'failed',
          text: out.stopped
            ? t('pfHeld.failedStopped', { reason: out.reason })
            : t('pfFix.failed', { action, reason: out.reason }),
        })
      },
      (error: unknown) => {
        finish({
          state: 'failed',
          text: t('pfFix.failed', { action, reason: errorMessage(error as ApiError) }),
        })
      },
    )
  }

  const busy =
    Object.values(rows).some((row) => row.state === 'running') || recheckMutation.isPending
  const inFlight: ActionGate = { disabled: true, disabledReason: t('reason.requestInFlight') }
  const control = (needsLock: boolean): ActionGate =>
    busy ? inFlight : gate(access, { perm: 'control', needsLock })
  const confirmGate = (): ActionGate =>
    confirmSource === null
      ? { disabled: true, disabledReason: t('reason.confirmSurfaceUnknown') }
      : control(false)
  const moduleOf = (row: Row): ModuleSnapshot | undefined =>
    row.module_id === null
      ? undefined
      : modules.data?.items.find((module) => module.module_id === row.module_id)
  const noModule: ActionGate = { disabled: true, disabledReason: t('pfFix.moduleUnknown') }

  const actionFor = (kind: PreflightFixKind, row: Row, key: string): ReactNode => {
    switch (kind) {
      case 'confirmUnload': {
        if (unloads.isPending || unloads.isError)
          return (
            <Button
              key="unload-unavailable"
              size="pill"
              disabled
              disabledReason={t(unloads.isPending ? 'pfFix.unloadsLoading' : 'pfFix.unloadsFailed')}
            >
              {t('pfFix.confirmUnload')}
            </Button>
          )
        const pending = (unloads.data?.items ?? []).filter(
          (unload) =>
            unload.confirmed_at === null &&
            (row.module_id === null || unload.module_id === row.module_id),
        )
        const occupied = row.code === 'HANDOFF_POINT_OCCUPIED_AT_START'
        const pointId = occupied
          ? row.module_id === null
            ? null
            : resolveHandoffPoint(
                row.item,
                pending.map((unload) => unload.point_id),
              )
          : row.item
        const open = pending.filter((unload) => pointId !== null && unload.point_id === pointId)
        const action = t('pfFix.confirmUnload')
        return open.map((unload) => (
          <Button
            key={`unload-${String(unload.unload_id)}`}
            onClick={() =>
              confirmSource === null
                ? undefined
                : run(key, action, async () => {
                    await confirmUnload.mutateAsync({
                      unloadId: String(unload.unload_id),
                      confirmSource,
                    })
                    void loads.refetch()
                  })
            }
            size="pill"
            {...confirmGate()}
          >
            {action}
          </Button>
        ))
      }
      case 'confirmLoad': {
        const open =
          loads.isPending || loads.isError
            ? []
            : (loads.data?.items ?? []).filter(
                (load) =>
                  load.confirmed_at === null &&
                  (row.item === null || load.point_id === row.item) &&
                  (row.module_id === null || load.module_id === row.module_id),
              )
        if (open.length === 0) {
          const reason = t(
            loads.isPending
              ? 'pfFix.loadsLoading'
              : loads.isError
                ? 'pfFix.loadsFailed'
                : 'pfFix.loadMissing',
          )
          return (
            <span className="pffix" key="load-unavailable">
              <Button size="pill" disabled disabledReason={reason}>
                {t('pfFix.confirmLoad')}
              </Button>
              <span className="small muted">{reason}</span>
            </span>
          )
        }
        return open.map((load) => (
          <Button
            key={`load-${String(load.load_id)}`}
            onClick={() => setLoadTarget({ key, load })}
            size="pill"
            {...confirmGate()}
          >
            {t('pfFix.confirmLoad')}
          </Button>
        ))
      }
      case 'releaseHandoff':
        return (
          <HandoffReleaseFix
            key={key}
            access={access}
            busy={busy}
            module={modules.isPending || modules.isError ? undefined : moduleOf(row)}
            operations={operations}
            pointId={row.item}
            onRun={(send) => run(key, t('pfFix.prepare'), send, true)}
          />
        )
      case 'initialize': {
        const module = moduleOf(row)
        const action = t('action.initializeModule')
        const initGate =
          module === undefined
            ? noModule
            : busy
              ? inFlight
              : initializeGate({
                  access,
                  operations,
                  controlOwner: module.control_owner,
                  allowedCommands: module.allowed_commands,
                  sending: initialize.isPending,
                }).gate
        return (
          <Button
            key="initialize"
            onClick={() =>
              module === undefined
                ? undefined
                : run(key, action, () => initialize.mutateAsync({ moduleId: module.module_id }))
            }
            size="pill"
            {...initGate}
          >
            {action}
          </Button>
        )
      }
      case 'clearSafety': {
        const action = t('action.clearSafety')
        return (
          <Button
            key="clearSafety"
            onClick={() => run(key, action, () => clearSafety.mutateAsync({}))}
            size="pill"
            {...control(true)}
          >
            {action}
          </Button>
        )
      }
      case 'forgetGrips': {
        const module = moduleOf(row)
        const action = t('action.forgetGrips')
        const forgetGate =
          module === undefined
            ? noModule
            : busy
              ? inFlight
              : moduleWriteGate({
                  access,
                  operations,
                  operationIds: FORGET_GRIPS_OPERATIONS,
                  controlOwner: module.control_owner,
                  sending: forget.isPending,
                })
        return (
          <ReasonActionButton
            actionLabel={action}
            confirmBody={t('moduleCtl.forgetGrips.confirmBody', {
              module: module?.module_id ?? row.module_id ?? '',
            })}
            description={t('moduleCtl.desc.forgetGrips')}
            gate={forgetGate}
            key="forgetGrips"
            onSend={(reason) => {
              if (module === undefined) return
              run(key, action, () => forget.mutateAsync({ moduleId: module.module_id, reason }))
            }}
            sending={forget.isPending}
          />
        )
      }
      case 'replaceWaste': {
        const container = (waste.data?.items ?? []).find((item) => item.container_id === row.item)
        if (container === undefined) return null
        const action = t('pfFix.replaceWaste')
        return (
          <Button
            key="replaceWaste"
            onClick={() =>
              run(key, action, () =>
                recordWaste.mutateAsync({ containerId: container.container_id }),
              )
            }
            size="pill"
            {...control(false)}
          >
            {action}
          </Button>
        )
      }
      case 'replenish': {
        const module = moduleOf(row)
        const supplyKind = supplyKindOf(row.code, module)
        if (supplyKind === undefined) return null
        return (
          <SupplyFix
            access={access}
            batchId={batchId}
            key="replenish"
            kind={supplyKind}
            module={module}
            onRun={(send) => run(key, t(`pfFix.supply.${supplyKind}`), send)}
            writeGate={control(false)}
          />
        )
      }
      case 'releaseStops':
        return (
          <StopsReleaseFix
            gate={control(true)}
            key="releaseStops"
            onRun={(send) => run(key, t('pfStops.action'), send, true)}
          />
        )
      case 'placeHeldRecreate': {
        /* 🔴 쥔 채 멈춘 6축 줄에만 선다 — 다른 «대기 아님» 줄에는 작업을 끊는 단추가 없다 */
        const module = moduleOf(row)
        if (module === undefined || !holdsTrayAtHandoff(module)) return null
        return (
          <HeldTrayFix
            batchId={batchId}
            gate={
              module.stale
                ? { disabled: true, disabledReason: t('reason.streamStale') }
                : control(true)
            }
            key="placeHeldRecreate"
            module={module}
            onRun={(flow) => runHeld(key, flow)}
          />
        )
      }
      case 'installLabware': {
        /* 🔴 자리 이름을 못 읽으면 단추가 없다 — 자리를 지어내지 않는다 */
        const target = lbw09Target(row.item)
        if (target === undefined) return null
        return (
          <InstallLabwareFix
            key="installLabware"
            module={moduleOf(row)}
            onRun={(send) => run(key, t('pfFix.installLabware'), send)}
            target={target}
            writeGate={control(true)}
          />
        )
      }
    }
  }

  const rowExtra = (row: Row): PreflightRowExtra | undefined => {
    const kinds = preflightFixesFor(row.code, row.item)
    const key = preflightRowKey(row)
    const state = rows[key]
    const buttons = kinds.map((kind) => actionFor(kind, row, key))
    const after =
      state === undefined ? null : (
        <p
          className={state.state === 'failed' ? 'pffix__line vissue' : 'pffix__line small muted'}
          role={state.state === 'failed' ? 'alert' : 'status'}
        >
          {state.text}
        </p>
      )
    if (kinds.length === 0 && state === undefined) return undefined
    return { actions: <span className="pffix">{buttons}</span>, after }
  }

  const passedNow = result.passed && !result.blockers.some((blocker) => blocker.verdict === 'fail')

  return (
    <>
      <div className="pffix__bar pffix__bar--top">
        <Button size="pill" disabled disabledReason={t('pfFix.batchPreservingOnly')}>
          {t('pfAll.action')}
        </Button>
      </div>
      {flowNotice === null ? null : (
        <p
          className={
            flowNotice.state === 'failed' ? 'pffix__line vissue' : 'pffix__line small muted'
          }
          role={flowNotice.state === 'failed' ? 'alert' : 'status'}
        >
          {flowNotice.text}
        </p>
      )}
      <PreflightReport
        result={result}
        rowExtra={rowExtra}
        presentation={presentation}
        hideTitle={hideTitle}
        startedOnce
      />
      {passedNow ? (
        <p className="pffix__line" role="status">
          {t('pfFix.passed')}
        </p>
      ) : null}
      <div className="pffix__bar">
        <Button
          onClick={() => {
            void loads.refetch()
            void unloads.refetch()
            recheck()
          }}
          size="pill"
          {...(busy ? inFlight : {})}
        >
          {t('pfFix.recheck')}
        </Button>
        {recheckMutation.isError ? (
          <span className="vissue" role="alert">
            {errorMessage(recheckMutation.error)}
          </span>
        ) : null}
      </div>

      {loadTarget === null ? null : (
        <LoadConfirmWithDefault
          key={loadTarget.load.load_id}
          error={undefined}
          gate={confirmGate()}
          load={loadTarget.load}
          onClose={() => setLoadTarget(null)}
          onSubmit={(report: LoadConfirmReport) => {
            const target = loadTarget
            setLoadTarget(null)
            if (confirmSource === null) return
            run(target.key, t('pfFix.confirmLoad'), () =>
              confirmLoad.mutateAsync({
                loadId: String(target.load.load_id),
                confirmSource,
                ...report,
              }),
            )
          }}
        />
      )}
    </>
  )
}

/** 적재 확약을 아직 기다리는 줄이 있다 — 조치 직후 서버가 적재 건을 늦게 연다 */
function trayLoadPending(result: PreflightResult): boolean {
  return (
    !result.passed &&
    result.blockers.some(
      (blocker) => blocker.code === 'PREFLIGHT_TRAY_NOT_LOADED' && blocker.verdict === 'fail',
    )
  )
}
